import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'

import { ContentItem } from '@thg-commerce/enterprise-components'
import {
  i18n,
  useEnterpriseContext,
  useHorizonSessionSettings,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { HorizonSessionSettings } from '@thg-commerce/enterprise-core/src/EnterpriseContext/types'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import {
  Feature,
  ProductMarketedSpecialOffer,
  ProductVariant,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-theme'
import { sanitizeCookie } from '@thg-commerce/enterprise-utils'
import { ProductRecommendations } from '@thg-commerce/enterprise-widget-product-recommendations/src/ProductRecommendations'
import { ProductPriceProps } from '@thg-commerce/gravity-elements'
import { ColourChoice } from '@thg-commerce/gravity-elements/Swatch'

import { RecentlyViewedProducts as RECENTLY_VIEWED } from '../../../../graphql/RecentlyViewedProducts.graphql'

import { ProductRecSkeleton } from './ProductRecSkeleton'
import { ListWrapper, Title } from './styles'

interface RecentlyViewedData {
  url: string
  title: string
  brand: {
    name: string
    imageUrl?: string
  }
  colourSwatches?: ColourChoice[]
  sku: number
  externalIdentifier?: string
  marketedSpecialOffer?: ProductMarketedSpecialOffer
  price: ProductPriceProps
  priceRange?: string
  rrpRange?: string
  image: string
  reviews: {
    averageScore: number
    total: number
  }
  inStock?: boolean
  isCheckStock?: boolean
  isOrderInStore?: boolean
  defaultVariant?: ProductVariant
  content?: ContentItem[]
}

const RECENTLY_VIEWED_PRODUCTS_COOKIE = 'recentlyViewedProducts'
const recentProducts = () => {
  const sanitizedValue = sanitizeCookie(RECENTLY_VIEWED_PRODUCTS_COOKIE)
  const productSkus: string[] = sanitizedValue ? sanitizedValue.split(',') : []

  return new Set<string>(productSkus)
}

export const RecentlyViewed = () => {
  const recentlyViewedSkus = recentProducts()

  const sessionSettings = useHorizonSessionSettings()
  const theme = useTheme()
  const {
    showPdpLinkWhenOutOfStock,
    enableClickAndCollect,
    enableVipPrice,
    linkedSkusEnabled,
    disableSavingOnRecentlyViewed,
  } = useSiteConfig()
  const { horizonFeatures } = useEnterpriseContext()
  const vipPriceEnabled =
    (enableVipPrice && horizonFeatures?.includes(Feature.VipPricingEnabled)) ||
    false

  const skipQuery = !recentlyViewedSkus || recentlyViewedSkus!.size < 1

  const i18nText = {
    recentlyViewedTitle: i18n('product.recentlyviewedproducts.title'),
    rrpLabel: i18n('product.price.rrp.text'),
  }

  const queryVariables = {
    ...sessionSettings,
    vipPriceEnabled,
    linkedSkusEnabled,
    skus: Array.from(recentlyViewedSkus),
    strict: false,
    clickAndCollectEnabled: !!enableClickAndCollect,
  }

  const { data, loading } = useQuery<{
    productBlockList: RecentlyViewedData[]
  }>(RECENTLY_VIEWED, {
    skip: skipQuery,
    variables: queryVariables,
  })

  const products = data?.productBlockList || []

  if (loading) {
    return <ProductRecSkeleton />
  }

  if (products.length) {
    return (
      <ListWrapper data-testid="recently-viewed-products">
        <Title
          textEntry={theme.widget.productRecommendations.title.font.entry}
          textStyle={theme.widget.productRecommendations.title.font.style}
          textTransform={
            theme.widget.productRecommendations.title.font.transform
          }
        >
          {i18nText.recentlyViewedTitle}
        </Title>
        <ProductRecommendations
          products={products}
          itemsPerSlide={
            theme.widget.productRecommendations.carousel.itemsPerSlide
          }
          trackingContext={RecommendationTrackingContext.DEFAULT}
          recommendationsStyle={theme.widget.productRecommendations}
          showPdpLinkWhenOutOfStock={showPdpLinkWhenOutOfStock}
          widgetTitle={i18nText.recentlyViewedTitle}
          carouselSize={theme.widget?.productRecommendations?.carousel?.size}
          enableSavePrice={disableSavingOnRecentlyViewed}
          displayAlternateStyle={true}
        />
      </ListWrapper>
    )
  }

  return null
}
